import React, { useState, useEffect } from "react";
import LeftSidePopupLayout from "../../../component/popup/modal-layout";
import XCloseIcon from "../../../component/icons/x-close";
import Style from "./style.module.css";
import Step1Form from "./Step1Form";
import Step2Form from "./Step2Form";
import Step3Form from "./Step3Form";
import Step4Form from "./Step4Form";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import { useFormik } from "formik";
import * as yup from "yup";
import InnerLoader from "../../../component/ui/loader/inner-loader";

export const STEP2_META_MAP = {
    q1: "product_knowledge",
    q2: "pricing_and_terms_clarity",
    q3: "misrepresentation",
    q4: "tone_and_courtesy",
    q5: "listening_and_empathy",
    q6: "objection_handling",
    q7: "policy_adherence",
    q8: "dissatisfaction_handled",
    q9: "missed_opportunity",
    q10: "flow",
};

const STEP3_META_MAP = {
    wrongSale: "flag_wrong_sale",
    trainingNeeded: "flag_training_needed",
    refundTrigger: "flag_refund_trigger",
    missedOpportunity: "flag_missed_opportunity",
    productionMisalignment: "flag_production_misalignment",
};

const AddFullModal = ({ onSubmit = () => { }, onClose = () => { }, onError = () => { }, callId, type }) => {
    const apiCall = useApiCall();
    const [step, setStep] = useState();
    const [stages, setStages] = useState([]);
    const [cTypes, setCTypes] = useState([]);
    const [agents, setAgents] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [suggestedActions, setSuggestedActions] = useState({});

    const [loading, setLoading] = useState(true);
    const [buttonLoading, setButtonLoading] = useState(false);

    // ---------------- Formik & Validation ----------------
    const validationSchema = yup.object({
        stage: yup.string().required("Required"),
        c_type: yup.string().required("Required"),
        agent_id: yup.string().required("Required"),
        customer_id: yup.string().required("Required"), // ab hamesha required
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            call_log_id: callId,
            stage: "",
            c_type: "",
            agent_id: "",
            customer_id: "",
            salesAccuracy: {},
            flags: {},
            remarks: {
                summary: "",
                suggestedAction: "",
                recordingLinks: [""],
                timestampNotes: [""],
                screenshotImages: [],
            },
        },
        validationSchema,
        onSubmit: async values => { },
    });

    // ---------------- Fetch Edit Data ----------------
    useEffect(() => {
        const fetchEditData = async () => {
            if (!callId) return;
            setLoading(true);
            try {
                // 1. Fetch edit call data
                const res = await apiCall({
                    url: "edit-call-log",
                    method: "post",
                    data: { id: callId, type: type },
                });

                if (!res?.success) {
                    console.warn("edit-call-log API returned no success", res);
                    return;
                }

                const apiData = res?.data;

                // 2. Set current step
                setStep(res?.current_step);

                // 3. Set stages and cTypes
                const stagesData = apiData?.stages || apiData?.data?.stages || [];
                const cTypesData = apiData?.c_type || apiData?.data?.c_type || [];

                setStages(Array.isArray(stagesData) ? stagesData : Object.values(stagesData));
                setCTypes(Array.isArray(cTypesData) ? cTypesData : Object.values(cTypesData));

                if (apiData?.suggested_action) {
                    setSuggestedActions(apiData.suggested_action);
                }

                // 4. Fetch agents and customers ONLY if current_step === 1
                if (res?.current_step === 1) {
                    const [agentsRes, custRes] = await Promise.all([
                        apiCall({ method: "get", url: "users/agents-list/6" }),
                        apiCall({ method: "get", url: "users/customers-list" }),
                    ]);

                    setAgents(agentsRes?.agents || []);
                    setCustomers(custRes?.customers || []);
                }

            } catch (err) {
                console.error("Error fetching edit-call-log", err);
            }
            finally {
                setLoading(false);
            }
        };

        fetchEditData();
    }, [callId, type]);

    const validateStep2 = () => {
        const errors = {};

        Object.keys(STEP2_META_MAP).forEach((qKey) => {
            if (!formik.values.salesAccuracy?.[qKey]) {
                errors[qKey] = "Required";
            }
        });

        return errors;
    };

    const validateStep3 = () => {
        const errors = {};

        Object.keys(STEP3_META_MAP).forEach((flagKey) => {
            const selected = formik.values.flags?.[flagKey] || [];
            const note = formik.values.flags?.[`${flagKey}_note`] || "";

            if (selected.length === 0 && !note.trim()) {
                errors[flagKey] = "Required";
            }
        });

        return errors;
    };

    const validateStep4 = () => {
        const errors = {};
        const { summary, suggestedAction } = formik.values.remarks;

        if (!summary?.trim()) {
            errors.summary = "Summary is required";
        }

        if (!suggestedAction?.trim()) {
            errors.suggestedAction = "Select an action";
        }

        return errors;
    };



    const handleSaveContinue = async () => {
        if (buttonLoading) return;

        setButtonLoading(true);

        try {
            /* ---------- STEP 1 ---------- */
            if (step === 1) {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                    formik.setTouched({
                        stage: true,
                        c_type: true,
                        agent_id: true,
                        customer_id: true,
                    });
                    return;
                }

                const res = await apiCall({
                    method: "post",
                    url: "save-meta-step-1",
                    data: {
                        call_log_id: callId,
                        stage: formik.values.stage,
                        c_type: formik.values.c_type,
                        agent_id: formik.values.agent_id,
                        customer_id: formik.values.customer_id,
                        type,
                    },
                });

                if (!res.success) return onError(res.message || "Step 1 failed");

                setStep(2);
                return;
            }

            /* ---------- STEP 2 ---------- */
            if (step === 2) {
                const step2Errors = validateStep2();
                if (Object.keys(step2Errors).length > 0) {

                    Object.keys(STEP2_META_MAP).forEach((qKey) => {
                        formik.setFieldTouched(`salesAccuracy.${qKey}`, true, false);
                    });

                    formik.setErrors({
                        ...formik.errors,
                        salesAccuracy: step2Errors,
                    });

                    return;
                }

                const meta = [];
                Object.entries(STEP2_META_MAP).forEach(([qKey, metaKey]) => {
                    meta.push({ meta_key: metaKey, meta_value: String(formik.values.salesAccuracy[qKey]) });
                    meta.push({
                        meta_key: `${metaKey}_description`,
                        meta_value: formik.values.salesAccuracy[`${qKey}_note`] || "",
                    });
                });

                const res = await apiCall({
                    method: "post",
                    url: "save-meta-step-2",
                    data: { call_log_id: callId, meta },
                });

                if (!res.success) return onError(res.message || "Step 2 failed");

                setStep(3);
                return;
            }

            /* ---------- STEP 3 ---------- */
            if (step === 3) {
                const step3Errors = validateStep3();

                if (Object.keys(step3Errors).length > 0) {
                    // mark all flags as touched
                    Object.keys(STEP3_META_MAP).forEach((flagKey) => {
                        formik.setFieldTouched(`flags.${flagKey}`, true, false);
                        formik.setFieldTouched(`flags.${flagKey}_note`, true, false);
                    });

                    formik.setErrors({
                        ...formik.errors,
                        flags: step3Errors,
                    });

                    return;
                }

                const meta = [];

                Object.entries(STEP3_META_MAP).forEach(([flagKey, metaKey]) => {
                    const selected = formik.values.flags[flagKey] || [];
                    const note = formik.values.flags[`${flagKey}_note`] || "";

                    meta.push({
                        meta_key: metaKey,
                        meta_value: selected.length ? selected.join(",") : "None of Them",
                    });

                    meta.push({
                        meta_key: `${metaKey}_description`,
                        meta_value: note,
                    });
                });

                const res = await apiCall({
                    method: "post",
                    url: "save-meta-step-3",
                    data: { call_log_id: callId, meta },
                });

                if (!res.success) return onError(res.message || "Step 3 failed");

                setStep(4);
                return;
            }

            /* ---------- STEP 4 ---------- */
            if (step === 4) {
                const step4Errors = validateStep4();

                if (Object.keys(step4Errors).length > 0) {
                    formik.setErrors({
                        remarks: {
                            summary: step4Errors.summary,
                            suggestedAction: step4Errors.suggestedAction,
                        },
                    });
                    return;
                }

                const { summary, suggestedAction, recordingLinks, timestampNotes, screenshotImages } =
                    formik.values.remarks;

                const payload = {
                    call_log_id: callId,
                    remarks: {
                        summary,
                        action: suggestedAction,
                        timestamp: timestampNotes.filter(Boolean).join(", "),
                        screenshots: screenshotImages,
                        recordings: recordingLinks.filter(Boolean),
                    },
                };

                const res = await apiCall({
                    method: "post",
                    url: "save-meta-step-4",
                    data: payload,
                });

                if (!res.success) return onError(res.message || "Step 4 failed");

                onSubmit("Call review saved successfully!", callId);
            }
        } catch (err) {
            console.error(err);
            onError("Something went wrong");
        } finally {
            setButtonLoading(false);
        }
    };


    const handleSkipStep3 = async () => {
        try {
            const meta = [
                {
                    meta_key: "step3_skipped",
                    meta_value: true,
                },
            ];

            await apiCall({
                method: "post",
                url: "save-meta-step-3",
                data: {
                    call_log_id: callId,
                    meta,
                },
            });

            setStep(4);
        } catch (err) {
            console.error("Error skipping step 3", err);
        }
    };


    return (
        <LeftSidePopupLayout popupStatus={true} className={`${Style.addMeModal} ${Style.leaveModal} !gap-0 rounded-xl`}>
            {/* HEADER */}
            {/* HEADER */}
            <div className={`${Style.modal_header} ${Style.addMeModal_head} flex flex-col gap-2`}>
                <div className="flex justify-between items-center">
                    <h4>Add Call Review</h4>
                    <div onClick={onClose} className={`${Style.close_btn} cursor-pointer`}>
                        <XCloseIcon />
                    </div>
                </div>

                {/* PROGRESS BAR */}
                <div className="w-full bg-[#E9EAEB] rounded-sm h-[2px]">
                    <div
                        className="bg-[#2563eb] h-[2px] rounded-sm transition-all duration-300"
                        style={{
                            width: step === 1 ? "0%" :
                                step === 2 ? "25%" :
                                    step === 3 ? "50%" :
                                        step === 4 ? "75%" : "0%"
                        }}
                    />
                </div>
            </div>

            {/* BODY */}
            {loading ? (
                <InnerLoader />
            ) : (
                <>
                    <div className={Style.addMeModal_body}>
                        <div className={Style.dcmb__card}>
                            <div className={Style.dcmb__cardBody}>
                                {step === 1 && <Step1Form formik={formik} stages={stages} cTypes={cTypes} agents={agents} customers={customers} />}
                                {step === 2 && <Step2Form formik={formik} />}
                                {step === 3 && <Step3Form formik={formik} />}
                                {step === 4 && <Step4Form formik={formik} suggestedActions={suggestedActions} />}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* FOOTER */}
            <div className={Style.dcmb__cardFoot}>
                {step === 3 && (
                    <button
                        type="button"
                        className={Style.dcmb__cancel}
                        onClick={() => handleSkipStep3()}
                    >
                        Skip
                    </button>
                )}
                <button type="button" className={Style.dcmb__submit} onClick={handleSaveContinue}>
                    Save & Continue
                </button>
            </div>
        </LeftSidePopupLayout>
    );
};

export default AddFullModal;
